import { cognitoApi } from '../cognitoApi';
import { CognitoAPIResponse } from '../types';
import { UserNote } from '../users/types';
import { attachIds } from '../utils';

import {
  ShortStaff,
  Staff,
} from './types';
import {
  toShortStaffs, toStaff,
} from './utils';

// Define a service using a base URL and expected endpoints
export const staffApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    newStaff: build.mutation<any, {attributes: any}>({
      query(arg) {
        const { attributes } = arg;

        return {
          url: 'staff/new',
          body: attributes,
          method: 'POST',
        };
      },
      invalidatesTags: ['Users', 'Staff'],
    }),
    getStaff: build.query<CognitoAPIResponse<Staff>, {staffId: string}>({
      query: (arg) => `staff/${arg.staffId ?? ''}`,
      providesTags: (result, error, arg) => [{ type: 'Staff', id: arg?.staffId }],
      transformResponse(response: any) {
        return toStaff(response);
      },
    }),
    getStaffs: build.query<CognitoAPIResponse<ShortStaff[]>, {
      search?: string,
      excludeDisabled?: boolean,
      group?: string,
    }>({
      query: (arg) => {
        const { excludeDisabled, search, group } = arg;
        return {
          url: 'staff/query',
          method: 'GET',
          params: {
            excludeDisabled, search, group,
          },
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'Staff' as const, id })), 'Staff']
        : ['Staff']),
      transformResponse(response: any) {
        return toShortStaffs(response);
      },
    }),
    updateStaff: build.mutation<any, {staffId: string, newAttributes: any}>({
      query(arg) {
        const { staffId, newAttributes } = arg;
        return {
          url: `staff/${staffId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Staff', id: arg?.staffId }],
    }),
    newStaffNote: build.mutation<any, {
      staffId: string,
      note: string,
      attributes?: {
        assignee?: string,
        dueDatetime?: string,
      }
    }>({
      query(arg) {
        const { staffId, note, attributes } = arg;

        return {
          url: `staff/${staffId}/notes/new`,
          body: {
            note,
            ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: ['UserNotes'],
    }),
    getStaffNotes: build.query<CognitoAPIResponse<UserNote[]>, {
      staffId: string,
      active?: boolean,
    }>({
      query(arg) {
        const { staffId, active } = arg;

        return {
          url: `staff/${staffId}/notes/query`,
          params: { active },
          method: 'GET',
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'UserNotes' as const, id })), 'UserNotes']
        : ['UserNotes']),
      transformResponse(response: any) {
        return attachIds(response, (entry: UserNote) => `${entry.userId}#${entry.creationDatetime}`);
      },
    }),
    updateStaffNote: build.mutation<any, {
      staffId: string,
      creationDatetime: number,
      newAttributes: any
    }>({
      query(arg) {
        const { staffId, creationDatetime, newAttributes } = arg;

        return {
          url: `staff/${staffId}/notes/${creationDatetime}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // Every user note should be invalidated if the due date of the note is updated, as
      // a note without a due date could gain a due date; in which case it should show
      // up in the follow up page
      invalidatesTags: (result, error, arg) => (arg.newAttributes.dueDatetime
        ? ['UserNotes']
        : [{ type: 'UserNotes', id: `${arg.staffId}#${arg.creationDatetime}` }]),
    }),
    deleteStaffNote: build.mutation<any, {
      staffId: string,
      creationDatetime: number,
    }>({
      query(arg) {
        const { staffId, creationDatetime } = arg;

        return {
          url: `staff/${staffId}/notes/${creationDatetime}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'UserNotes', id: `${arg.staffId}#${arg.creationDatetime}` },
      ],
    }),
  }),
});

export const {
  useLazyGetStaffQuery,
  useNewStaffMutation,
  useGetStaffQuery,
  useGetStaffsQuery,
  useUpdateStaffMutation,
  useNewStaffNoteMutation,
  useGetStaffNotesQuery,
  useDeleteStaffNoteMutation,
  useUpdateStaffNoteMutation,
} = staffApi;
